import { useCallback, useMemo, useState } from "react"
import { GetApp } from "../../App"
import { GetShip } from "../../App/Context"
import { Ship } from "../../App/Ship"
import { useEditState } from "../../App/useEditState"
import { DepthWatchAdvice } from "./DepthWatchAdvice"
import { BlockField, BlockRow } from "../../Components/Block"
import { EditGroup, EditGroupFieldDefinitions } from "../../Components/EditGroup"
import { UserAdviceDialog } from "../../Components/UserAdvice/UserAdvice"
import { OverlayViewDefinition } from "../OverlayView"
import { logger } from "../../App/Logger"
import { DepthThresholdFieldsView } from "../NMEAConfiguration/DepthThresholdFieldsView"
import { UserAdviceViewsData } from "./Context"
import { UserAdviceDepthWatchViewProps } from "./Params"
import { useSettingsWriters } from "../../Data"

const editableFields: EditGroupFieldDefinitions[] = [{ name: "thresholds", type: "object", fields: [{ name: "depth", type: "object", fields: [{ name: "warning", type: "float" }, { name: "alarm", type: "float" }] }] }];

// The standalone underway depth-watch control panel (#990). Unlike the anchor
// monitor's dialog, it has no per-instance geometry -- editing the warning/alarm
// fields here edits the ship-wide default threshold directly (the same value the
// NMEA Conditions settings page shows), since the underway watch always applies
// that default. Threshold edits are saved through an explicit Save button (not
// save-as-you-type) and merged client-side with the ship's existing threshold
// settings, since the settings save is a shallow top-level JSON merge and would
// otherwise wipe sibling fields like anchorDrift. Enable / disable through the
// dedicated lifecycle route, and drive acknowledge / snooze / clear on any open
// alert this watch currently claims.
export function UserAdviceDepthWatchView({ advice, state, initFailed, initSaveFailed }: UserAdviceDepthWatchViewProps) {
  const app = GetApp();
  const t = app.getT("UserAdviceDepthWatchView");
  const ship = GetShip();
  const settingsWriters = useSettingsWriters(ship.id);
  const [edited, _onEdited, _resetEdited] = useEditState();

  const [failed, setFailed] = useState(initFailed ?? false);
  const [saveFailed, setSaveFailed] = useState(initSaveFailed ?? false);

  const userAdvice = useMemo(() => advice.parent as DepthWatchAdvice, [advice]);
  const enabled = userAdvice.enabled;
  const thresholds = useMemo(() => Ship.shipSettings(ship).thresholds, [ship]);

  // Only dismiss the dialog once the update actually succeeds — e.g. a user
  // without sufficient permission gets a 401, which must stay visible instead
  // of being swallowed behind an optimistically-closed dialog (#1146).
  const submit = useCallback(async (enable: boolean) => {
    setFailed(false);
    try {
      await UserAdviceViewsData.depthWatch(app.apiClient, { enabled: enable });
      state.setSelected(undefined);
    } catch (e) {
      logger.error("depth watch update", e);
      setFailed(true);
    }
  }, [app, state]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  const saveThresholds = useCallback(async (_data: FormData, values: any) => {
    setSaveFailed(false);
    await settingsWriters.updateSettings({ thresholds: { ...thresholds, depth: values.thresholds?.depth } });
  }, [settingsWriters, thresholds]);

  return (
    <UserAdviceDialog className="UserAdviceDepthWatchView">
      <BlockRow className="block-row-infotext block-row-one-row">
        {t("text")}
      </BlockRow>
      <EditGroup
        changed={edited}
        editableFields={editableFields}
        manualSave={true}
        onSave={saveThresholds}
        onSaveError={(error) => {
          if (error == undefined) {
            setSaveFailed(false);
            return;
          }
          logger.error("depth threshold save", error);
          setSaveFailed(true);
        }}
      >
        <DepthThresholdFieldsView thresholds={thresholds?.depth} compact={false} onEdited={_onEdited} />
        {saveFailed && <BlockRow className="block-row-top20"><BlockField><span className="UserAdviceDepthWatchView-save-error text-error">{t("save_error")}</span></BlockField></BlockRow>}
        {failed && <BlockRow className="block-row-top20"><BlockField><span className="UserAdviceDepthWatchView-error text-error">{t("error")}</span></BlockField></BlockRow>}
        <BlockRow actions={true} className="block-row-gap">
          <BlockField><button className="button" type="button" onClick={() => state.setSelected(undefined)}>{t("cancel")}</button></BlockField>
          <BlockField><button className="button" type="submit">{t("save")}</button></BlockField>
          <BlockField><button className="button default" type="button" onClick={() => submit(!enabled)}>{t(enabled ? "disable" : "enable")}</button></BlockField>
        </BlockRow>
      </EditGroup>
    </UserAdviceDialog>
  );
}

// This dialog's own overlay attributes (#1171), declared here rather than in
// `UserAdviceOverlayView` so the dialog keeps deciding how it presents itself. It
// takes its title straight from the advice whose button opened it, and stays
// closable unless that advice asked otherwise. Deliberately hook-free -- one of
// these is called per open advice, so a hook would vary React's hook count.
export function userAdviceDepthWatchOverlay(props: UserAdviceDepthWatchViewProps): OverlayViewDefinition {
  return {
    close: !props.advice.props.hideClose,
    title: props.advice.props.title,
    render: () => <UserAdviceDepthWatchView {...props} />,
  };
}
